import { Signal } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { COUPON_TYPE } from '../../model/enums/coupon-type.enum';
import { ROLL_CONVENTION_ENUM } from '../../model/enums/roll-convention.enum';
import { DropDownOption } from '../../model/interfaces/drop-down-option.interface';
import { PayoffSchedule } from '../../model/interfaces/payoff-schedule.interface';

export const SCHEDULE_TYPE_BY_PRODUCT = new Map([
  ['AUTOCALL', 'OBS_PRIMARY'],
  ['CLN', 'ACCRUAL_PRIMARY'],
  ['FIXED_TO_FLOATING', 'ACCRUAL_PRIMARY'],
  ['FIXED_RATE_NOTE', 'ACCRUAL_PRIMARY'],
  ['FLOATING_RATE_NOTE', 'ACCRUAL_PRIMARY'],
]);

export const SCHEDULE_LAG_ITEMS = [
  { id: '0B', displayValue: 'None' },
  { id: '1B', displayValue: '1B' },
  { id: '2B', displayValue: '2B' },
  { id: '3B', displayValue: '3B' },
  { id: '4B', displayValue: '4B' },
  { id: '5B', displayValue: '5B' },
  { id: '10B', displayValue: '10B' },
];

export type SetupCommonFieldsProperties = {
  emitEvent?: boolean;
  skipScheduleResetting?: boolean;
};

export const CALL_NOTICE_LAG_BASE_TYPES: DropDownOption[] = [
  { id: 'PAYMENT_ADJUSTED', displayValue: 'Payment Adjusted' },
  { id: 'PAYMENT_UNADJUSTED', displayValue: 'Payment Unadjusted' },
];

export const PAYMENT_LAG_BASE_OPTIONS: DropDownOption[] = [
  { id: 'ADJUSTED', displayValue: 'Adjusted' },
  { id: 'UNADJUSTED', displayValue: 'Unadjusted' },
];

export enum ScheduleStrategyKeys {
  CALL_FROM_COUPON = 'CALL_FROM_COUPON',
  COUPON_FROM_CALL = 'COUPON_FROM_CALL',
  INDEPENDENT_COUPON_AND_CALL = 'INDEPENDENT_COUPON_AND_CALL',
  COUPON_ONLY = 'COUPON_ONLY',
  CALL_ONLY = 'CALL_ONLY',
}

export const SCHEDULE_STRATEGY_TYPES: DropDownOption[] = [
  {
    id: ScheduleStrategyKeys.CALL_FROM_COUPON,
    displayValue: 'Derive Call from Coupon schedule',
  },
  {
    id: ScheduleStrategyKeys.COUPON_FROM_CALL,
    displayValue: 'Derive Coupon from Call schedule',
  },
  {
    id: ScheduleStrategyKeys.INDEPENDENT_COUPON_AND_CALL,
    displayValue: 'Independent Coupon and Call schedules',
  },
  {
    id: ScheduleStrategyKeys.COUPON_ONLY,
    displayValue: 'Coupon schedule Only',
  },
  { id: ScheduleStrategyKeys.CALL_ONLY, displayValue: 'Call schedule Only' },
];

export enum IrregularPeriodsKeys {
  SHORT_FIRST = 'SHORT_FIRST',
  SHORT_LAST = 'SHORT_LAST',
  LONG_FIRST = 'LONG_FIRST',
  LONG_LAST = 'LONG_LAST',
}

export const IRREGULAR_PERIODS: DropDownOption[] = [
  { id: IrregularPeriodsKeys.SHORT_FIRST, displayValue: 'Short First' },
  { id: IrregularPeriodsKeys.LONG_FIRST, displayValue: 'Long First' },
  { id: IrregularPeriodsKeys.SHORT_LAST, displayValue: 'Short Last' },
  { id: IrregularPeriodsKeys.LONG_LAST, displayValue: 'Long Last' },
];

export const ROLL_MODAL_ROLL_CONVENTION_OPTIONS: DropDownOption[] = [
  { id: ROLL_CONVENTION_ENUM.FOLLOWING, displayValue: 'Following' },
  {
    id: ROLL_CONVENTION_ENUM.MODIFIED_FOLLOWING,
    displayValue: 'Modified following',
  },
  { id: ROLL_CONVENTION_ENUM.PRECEDING, displayValue: 'Preceding' },
];

export const COUPON_TYPES_TO_SHOW_OBS_DATES: string[] = [
  COUPON_TYPE.CALL,
  COUPON_TYPE.CONDITIONAL,
  COUPON_TYPE.DIGITAL,
  COUPON_TYPE.FIXED_TO_CALL,
  COUPON_TYPE.FIXED_TO_DIGITAL,
  COUPON_TYPE.FIXED_TO_FORWARD,
  COUPON_TYPE.FORWARD,
  COUPON_TYPE.LOWEST_FX_PERFORMANCE,
  COUPON_TYPE.SNOWBALL,
  COUPON_TYPE.RANGE_ACCRUAL,
];

export interface SchedulePayload {
  scheduleType: string;
  type: string;
  frequency: string;
  pmtScheduleLag: string;
  pmtScheduleLagBase: string;
  pmtRollConvention: string;
  pmtRollConfig?: RollConfig | string;
  obsRollConfig?: RollConfig;
  barrier?: ScheduleObservationDateSettings;
  schedule?: FormGroup<any> | FormArray<any> | PayoffSchedule[];
}

export interface RollConfig {
  endFirstPeriod: string;
  startLastPeriod: string;
}

export interface ScheduleObservationDateSettings {
  obsScheduleLag: string;
  obsScheduleLagBase: string;
  obsRollConvention: string;
  obsFrequency: string;
  fixFrequency: string;
  obsRollConfig?: RollConfig;
}

export interface CouponSchedulePayload extends SchedulePayload {
  accrualPeriodType: string;
  accrualRollConvention: string;
  dayCount: string;
  accrualRollConfig?: RollConfig;
  fixedPeriods?: string;
  floatingPeriods?: string;
  referenceRate: {
    assetId: string;
    calendarCodes: string[];
    publishingLag?: string;
    settlementLag?: string;
  };
  rateReset?: string;
  rateCalculationMethod?: string;
  rateResetSuspensionLength?: string;
  rateResetLookbackShift?: string;
  rateObservationPeriodShift?: string;
  rateObsPeriodShiftExtraCalendars?: string[];
  rateCalculationPeriodEndType?: string;
  performance?: ScheduleObservationDateSettings;
}

export interface ParticipationSchedulePayload {
  upside: AdjustmentSchedulePayload;
  downside: SchedulePayload;
  base: SchedulePayload;
}

export interface AdjustmentSchedulePayload extends SchedulePayload {
  adjustmentRollConfig?: RollConfig | null;
}

export interface InvestorExerciseSchedulePayload {
  frequency: string | null;
  scheduleCalendar: string | null;
}

export interface CallSchedulePayload extends SchedulePayload {
  callNoticeLag: string;
  callNoticeLagBaseType: string;
  deferredPeriods: number;
  lastPeriodCallable: boolean;
}

export interface SchedulesVisibility {
  adjustment?: Signal<boolean>;
  coupon?: Signal<boolean>;
  earlyRedemption?: Signal<boolean>;
  investorExercise?: Signal<boolean>;
  participation?: Signal<boolean>;
}
